import { Platform } from 'react-native';

import { isStandaloneDisplay } from '../../services/pwa/installPrompt';
import { useSafeViewport } from './useSafeViewport';

/**
 * Provides reactive device, platform, orientation, and breakpoint flags.
 *
 * Uses `useWindowDimensions` so values update automatically on resize/orientation change,
 * and exposes a small set of derived helpers used across responsive layouts.
 *
 * Breakpoints:
 * - `isXs`: width < 600
 * - `isSm`: 600 <= width < 768
 * - `isMd`: 768 <= width < 1200
 * - `isLg`: 1200 <= width < 1660
 * - `isXLg`: width >= 1660
 * - `isTablet`: 768 <= width < 980
 * - `isMedium`: 980 < width < 1200
 *
 * Platform helpers:
 * - `isWeb`, `isIOS`, `isAndroid`, `isNative`
 * - `isStandalone`: the web app launched from the home screen (installed PWA), so there is
 *   no browser chrome around it. Always false on native.
 *
 * Layout helpers:
 * - `isPortrait`, `isLandscape`, `isMobile`, `isDesktop`
 * - `safeAreaWidth`, `safeAreaHeight`
 *
 * @returns An object containing viewport dimensions and derived flags for responsive UI logic.
 */
export const useDevice = () => {
	const { width, height, insets } = useSafeViewport();

	const isPortrait = height >= width;
	const isLandscape = width > height;

	const isWeb = Platform.OS === 'web';
	const isIOS = Platform.OS === 'ios';
	const isAndroid = Platform.OS === 'android';
	const isNative = isIOS || isAndroid;
	const isStandalone = isWeb && isStandaloneDisplay();

	// Define breakpoints
	const isXs = width < 600;
	const isSm = width >= 600 && width < 768;
	const isMd = width >= 768 && width < 1200;
	const isLg = width >= 1200 && width < 1660;
	const isXLg = width >= 1660;
	const isTablet = width >= 768 && width < 980;
	const isMedium = width > 980 && width < 1200;
	const isDesktop = isWeb && (isLg || isMd || isXLg) && !isPortrait;
	const isWebM = isWeb && width < 1200;
	const isMobile = isNative || isXs || isSm;

	// USE THIS FOR RESPONSIVE LAYOUTS INSTEAD OF RAW width/height (MOBILE SAFETY + ORIENTATION HANDLING)
	const safeAreaWidth = width;
	const safeAreaHeight = height;

	return {
		width,
		height,
		safeAreaWidth,
		safeAreaHeight,
		insets,
		isPortrait,
		isMobile,
		isXs,
		isSm,
		isMd,
		isLg,
		isXLg,
		isDesktop,
		isWebM,
		isTablet,
		isMedium,
		isLandscape,
		isWeb,
		isIOS,
		isAndroid,
		isNative,
		isStandalone,
	};
};
